import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Tag, Pencil, Lock } from "lucide-react";
import PhoneInput from "../../components/PhoneInput";

const EMPTY = { code: "", discount_type: "percent", discount_value: 10, max_uses: 100, active: true, is_private: false, linked_phone: "" };

export default function AdminCoupons() {
    const [coupons, setCoupons] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // coupon being edited (null = create)
    const [form, setForm] = useState(EMPTY);

    const load = async () => {
        const { data } = await api.get("/coupons");
        setCoupons(data);
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY);
        setOpen(true);
    };

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            code: c.code,
            discount_type: c.discount_type,
            discount_value: c.discount_value,
            max_uses: c.max_uses,
            active: c.active !== false,
            is_private: !!c.is_private,
            linked_phone: c.linked_phone ? c.linked_phone.replace(/^\+91/, "") : "",
        });
        setOpen(true);
    };

    const save = async (e) => {
        e.preventDefault();
        const phoneFull = form.linked_phone ? `+91${form.linked_phone}` : "";
        if (form.is_private && !/^\+91[6-9]\d{9}$/.test(phoneFull)) {
            toast.error("Private coupon needs a valid 10-digit phone starting with 6-9");
            return;
        }
        try {
            if (editing) {
                const payload = {
                    discount_type: form.discount_type,
                    discount_value: parseInt(form.discount_value) || 0,
                    max_uses: parseInt(form.max_uses) || 1,
                    active: form.active,
                    is_private: form.is_private,
                    linked_phone: form.is_private ? phoneFull : null,
                };
                await api.put(`/coupons/${editing.id}`, payload);
                toast.success("Coupon updated");
            } else {
                const payload = {
                    code: form.code,
                    discount_type: form.discount_type,
                    discount_value: parseInt(form.discount_value) || 0,
                    max_uses: parseInt(form.max_uses) || 1,
                    active: form.active,
                    is_private: form.is_private,
                    linked_phone: form.is_private ? phoneFull : null,
                };
                if (payload.is_private) payload.max_uses = 1;
                await api.post("/coupons", payload);
                toast.success("Coupon created");
            }
            setOpen(false);
            setForm(EMPTY);
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete coupon?")) return;
        await api.delete(`/coupons/${id}`);
        load();
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Coupons</h1>
                        <p className="text-slate-400 text-sm mt-1">Promotional codes — public or private (1-time per phone).</p>
                    </div>
                    <button onClick={openCreate} data-testid="new-coupon-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> New Coupon
                    </button>
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {coupons.map(c => (
                        <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-lg p-5 relative" data-testid={`coupon-${c.code}`}>
                            <div className="flex items-center gap-2 text-[#00FF66]">
                                <Tag className="w-4 h-4" />
                                <span className="font-display font-bold text-2xl uppercase tracking-tight">{c.code}</span>
                                {c.is_private && (
                                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-sky-500/20 text-sky-300 border border-sky-500/40 px-2 py-0.5 rounded-full" data-testid={`badge-private-${c.code}`}>
                                        <Lock className="w-3 h-3" /> Private
                                    </span>
                                )}
                                {c.active === false && (
                                    <span className="ml-1 text-[10px] uppercase tracking-widest bg-slate-700/60 text-slate-300 px-2 py-0.5 rounded-full">Inactive</span>
                                )}
                            </div>
                            <div className="mt-2 text-sm text-slate-300">
                                {c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} flat`}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">{c.uses}/{c.max_uses} used</div>
                            {c.is_private && c.linked_phone && (
                                <div className="mt-1 text-xs text-sky-300">→ {c.linked_phone}</div>
                            )}
                            <div className="absolute top-3 right-3 flex items-center gap-2">
                                <button onClick={() => openEdit(c)} data-testid={`edit-coupon-${c.id}`}
                                    className="text-slate-500 hover:text-sky-400">
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button onClick={() => remove(c.id)} data-testid={`del-coupon-${c.id}`}
                                    className="text-slate-500 hover:text-red-400">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {open && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <form onSubmit={save} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="coupon-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editing ? "Edit" : "New"} Coupon</h3>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Code</label>
                                <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required disabled={!!editing} data-testid="coupon-code-input"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66] uppercase disabled:opacity-60" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Type</label>
                                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} data-testid="coupon-type-select"
                                    className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                    <option value="percent">Percent (%)</option>
                                    <option value="flat">Flat (₹)</option>
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Value</label>
                                    <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} data-testid="coupon-value-input"
                                        className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Max Uses</label>
                                    <input type="number" value={form.is_private ? 1 : form.max_uses} disabled={form.is_private} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} data-testid="coupon-maxuses-input"
                                        className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white disabled:opacity-60" />
                                </div>
                            </div>

                            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="coupon-private-toggle">
                                <input type="checkbox" checked={form.is_private}
                                    onChange={(e) => setForm({ ...form, is_private: e.target.checked, max_uses: e.target.checked ? 1 : form.max_uses })}
                                    className="w-4 h-4 accent-sky-400" />
                                <span className="text-sm text-slate-200 font-bold uppercase tracking-widest">Private (1-time, 1 phone)</span>
                            </label>

                            {form.is_private && (
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Linked Phone</label>
                                    <PhoneInput value={form.linked_phone} onChange={(v) => setForm({ ...form, linked_phone: v })} testid="coupon-phone-input" />
                                    <p className="text-[11px] text-slate-500 mt-1">Only this phone can redeem the coupon. Auto-locked to 1 use.</p>
                                </div>
                            )}

                            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="coupon-active-toggle">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#00FF66]" />
                                <span className="text-sm text-slate-200 font-bold uppercase tracking-widest">Active</span>
                            </label>

                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" data-testid="save-coupon-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">{editing ? "Update" : "Save"}</button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
